import React, { useReducer, createContext, useContext, ReactNode, Dispatch } from 'react';

// 定义折叠状态类型
type CollapseStateType = { [id: number]: boolean };

// 初始折叠状态
const initialCollapseState: CollapseStateType = {};

// 定义动作类型
type CollapseActionType =
    | { type: 'TOGGLE_COLLAPSE'; id: number }
    | { type: 'BATCH_COLLAPSE'; ids: number[] }
    | { type: 'BATCH_EXPAND'; ids: number[] };

// 定义 reducer 函数
function collapseReducer(state: CollapseStateType, action: CollapseActionType): CollapseStateType {
    switch (action.type) {
        case 'TOGGLE_COLLAPSE':
            return {
                ...state,
                [action.id]: !state[action.id],
            };
        case 'BATCH_COLLAPSE':
            return action.ids.reduce(
                (acc, id) => {
                    acc[id] = true;
                    return acc;
                },
                { ...state }
            );
        case 'BATCH_EXPAND':
            return action.ids.reduce(
                (acc, id) => {
                    acc[id] = false;
                    return acc;
                },
                { ...state }
            );
        default:
            throw new Error(`Unknown action type: ${(action as CollapseActionType).type}`);
    }
}

// 定义折叠上下文类型
interface CollapseContextType {
    collapseState: CollapseStateType;
    dispatch: Dispatch<CollapseActionType>;
}

// 创建折叠上下文
const CollapseContext = createContext<CollapseContextType | undefined>(undefined);

// 使用自定义 hook 封装 Context 消费
const useCollapseContext = (): CollapseContextType => {
    const context = useContext(CollapseContext);
    if (!context) {
        throw new Error('useCollapseContext must be used within a CollapseProvider');
    }
    return context;
};

interface CollapseProviderProps {
    children: ReactNode;
}

const CollapseProvider: React.FC<CollapseProviderProps> = ({ children }) => {
    const [collapseState, dispatch] = useReducer(collapseReducer, initialCollapseState);

    return <CollapseContext.Provider value={{ collapseState, dispatch }}>{children}</CollapseContext.Provider>;
};

export { collapseReducer, initialCollapseState, CollapseContext, useCollapseContext };

export default CollapseProvider;
